import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Sparkles, ArrowRight } from "lucide-react";
import { publicApi } from "../lib/api";
import { useContent, renderHighlightedTitle } from "../lib/useContent";

const Hero = () => {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("tutte");
  const [categories, setCategories] = useState([]);
  const navigate = useNavigate();
  const { field } = useContent("home");

  useEffect(() => {
    publicApi.listCategories()
      .then((r) => setCategories(r.data || []))
      .catch(() => setCategories([]));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (category && category !== "tutte") params.set("cat", category);
    navigate(`/attivita?${params.toString()}`);
  };

  const pretitle = field("hero", "pretitle", "Sanluri · Sardegna");
  const headline = field("hero", "headline", "Il paese che vive, negozio dopo negozio.");
  const highlightTerms = field("hero", "highlight_terms", ["che vive"]);
  const description = field("hero", "description", "Un unico, grande centro commerciale a cielo aperto. Scopri le attività, gli artigiani e gli eventi che rendono Sanluri il cuore pulsante del Medio Campidano.");
  const imageUrl = field("hero", "image_url", "/images/hero-sanluri.webp");
  const searchPlaceholder = field("hero", "search_placeholder", "Cosa stai cercando a Sanluri?");
  const categoryAllLabel = field("hero", "category_all_label", "Tutte le categorie");
  const ctaLabel = field("hero", "cta_label", "Esplora");
  const popularPrefix = field("hero", "popular_prefix", "Popolari:");
  const popularChips = field("hero", "popular_chips", ["Pizza", "Ottico", "Pane sardo", "Estetista", "Bar"]);
  const heroImageSrc = imageUrl?.startsWith("http") ? imageUrl : `${process.env.PUBLIC_URL}${imageUrl}`;

  return (
    <section className="relative pt-36 lg:pt-44 pb-24 overflow-hidden noise-bg">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 -bottom-[200px] pointer-events-none overflow-hidden"
      >
        <div
          className="absolute inset-0 hero-city-image hero-image-drift"
          style={{ backgroundImage: `url(${heroImageSrc})` }}
        />
      </div>
      <div
        aria-hidden="true"
        className="absolute inset-x-0 top-0 -bottom-[200px] pointer-events-none"
        style={{
          background:
            "linear-gradient(90deg, rgba(250,248,243,0.84) 0%, rgba(250,248,243,0.78) 30%, rgba(250,248,243,0.45) 55%, rgba(250,248,243,0.10) 78%, rgba(250,248,243,0) 100%), radial-gradient(900px 500px at 10% 25%, rgba(232,245,236,0.62) 0%, rgba(232,245,236,0) 60%)",
        }}
      />

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
        {pretitle !== "" && (
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-stone-500 mb-8 animate-fade-in">
            <span className="w-8 h-px bg-stone-400" />
            <span>{pretitle}</span>
            <Sparkles className="w-3.5 h-3.5 text-[#12964A]" />
          </div>
        )}

        <h1 className="font-display text-[42px] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[92px] tracking-[-0.03em] text-stone-950 max-w-4xl animate-fade-in-up">
          {renderHighlightedTitle(headline, highlightTerms)}
        </h1>

        {description !== "" && (
          <p className="mt-10 max-w-xl text-lg md:text-xl text-stone-600 leading-relaxed animate-fade-in-up" style={{ animationDelay: "120ms" }}>
            {description}
          </p>
        )}

        <form
          onSubmit={handleSearch}
          className="mt-14 bg-white/95 backdrop-blur-md rounded-3xl p-3 md:p-4 flex flex-col md:flex-row items-stretch md:items-center gap-3 md:gap-2 max-w-4xl border border-white/60 ring-1 ring-black/[0.03] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_8px_24px_-4px_rgba(15,23,42,0.10),0_24px_48px_-12px_rgba(18,150,74,0.10)] hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_14px_32px_-4px_rgba(15,23,42,0.12),0_36px_64px_-16px_rgba(18,150,74,0.14)] hover:-translate-y-0.5 focus-within:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_14px_32px_-4px_rgba(15,23,42,0.12),0_36px_64px_-16px_rgba(18,150,74,0.14)] focus-within:border-[#12964A]/25 animate-fade-in-up transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
          style={{ animationDelay: "240ms" }}
        >
          <div className="flex items-center gap-3 flex-1 px-5 py-3 md:py-2">
            <Search className="w-5 h-5 text-stone-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full bg-transparent focus:outline-none text-stone-900 placeholder:text-stone-400 py-2"
            />
          </div>
          <div className="hidden md:block w-px h-8 bg-stone-200" />
          <div className="flex items-center gap-3 px-5 py-3 md:py-2 md:min-w-[260px]">
            <MapPin className="w-5 h-5 text-stone-400 flex-shrink-0" />
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              aria-label="Filtra per categoria"
              className="w-full bg-transparent focus:outline-none text-stone-900 py-2 text-sm md:text-base"
            >
              <option value="tutte">{categoryAllLabel}</option>
              {categories.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="group relative overflow-hidden inline-flex items-center justify-center gap-2 bg-stone-950 hover:bg-[#12964A] text-white rounded-2xl px-7 py-4 text-sm md:text-[15px] transition-all duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] hover:shadow-[0_10px_28px_-6px_rgba(18,150,74,0.45)] hover:-translate-y-0.5 active:translate-y-0 active:duration-150"
            data-testid="hero-search-cta"
          >
            <span className="absolute inset-0 bg-gradient-to-br from-white/0 via-white/[0.08] to-white/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            <span className="relative z-10">{ctaLabel}</span>
            <ArrowRight className="relative z-10 w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </form>

        {(popularChips && popularChips.length > 0) && (
          <div className="mt-6 flex flex-wrap items-center gap-2 text-sm text-stone-500 animate-fade-in-up" style={{ animationDelay: "320ms" }}>
            <span>{popularPrefix}</span>
            {popularChips.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setQuery(t);
                  navigate(`/attivita?q=${encodeURIComponent(t)}`);
                }}
                className="px-3.5 py-1.5 rounded-full border border-stone-200 bg-white/75 backdrop-blur-sm hover:border-[#12964A] hover:text-[#12964A] hover:bg-white hover:-translate-y-0.5 hover:shadow-[0_6px_14px_-4px_rgba(18,150,74,0.20)] active:translate-y-0 transition-all duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
              >
                {t}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Hero;
